import Link from "next/link";
import Image from "next/image";
import { ArrowRight, BadgeCheck, Users } from "lucide-react";

import LandingPageShell from "@/components/landing-page/layout/LandingPageShell";
import { CourseContentSection } from "@/components/catalog/CourseContentSection";
import { CourseRequirementsDescription } from "@/components/catalog/CourseRequirementsDescription";
import { CertificatePathCoursesSection } from "@/components/catalog/CertificatePathCoursesSection";

import { chunkTwoColumns, formatCompactCount, getCertificatePathLabel } from "@/lib/catalog/course-page";
import { CatalogPriceCard, type UiCourse } from "@/components/catalog/course-page/CatalogPriceCard";
import { MockRatingStars } from "@/components/catalog/course-page/MockRatingStars";

export function CourseLikeScreenshot({ course }: { course: UiCourse }) {
  const [learnLeft, learnRight] = chunkTwoColumns(course.learnItems);
  const highlightLine = course.heroHighlights.join(" · ");
  const certificatePathLabel = getCertificatePathLabel(course.catalogSlug, course.title);

  return (
    <LandingPageShell surface="white">
      {/* Dark hero like screenshot */}
      <section className="relative z-0 bg-slate-950 text-white min-h-[360px] md:min-h-[420px]">
        <div className="container relative mx-auto px-0 pt-14 pb-16 md:pt-20 md:pb-20">
          <div className="mx-auto max-w-6xl pl-6 pr-0 md:pl-10 md:pr-2 lg:pl-12 lg:pr-0">
            <div className="min-w-0">
              <div className="mt-7 flex items-center gap-3">
                <Image
                  src="/images/InaraX_Logo_White.png"
                  alt="inaraX"
                  width={160}
                  height={48}
                  className="h-8 w-auto object-contain"
                  priority
                />
              </div>

              <h1 className="mt-2 text-3xl md:text-4xl font-black tracking-tight">{course.title}</h1>

              {course.badgeText ? (
                <div className="mt-3 inline-flex items-center gap-2 rounded border border-white/25 bg-white/10 px-3 py-1 text-xs font-semibold text-white/90">
                  <span className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-white/30">
                    <span className="h-2 w-2 rounded-full bg-white/80" />
                  </span>
                  <span>{course.badgeText}</span>
                </div>
              ) : null}

              {course.description ? (
                <p className="mt-4 max-w-2xl text-sm md:text-base text-white/80 leading-relaxed">
                  {course.description}
                </p>
              ) : null}

              <div className="mt-5 text-sm text-white/75">
                <span className="font-semibold text-white/85">Created by</span>{" "}
                <span className="underline underline-offset-2">{course.providerName}</span>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-white/65">
                {course.lastUpdatedText ? <span>Last updated {course.lastUpdatedText}</span> : null}
                {course.lastUpdatedText ? <span className="text-white/30">|</span> : null}
                <span className="text-white/70">Instruction language: English</span>
              </div>
            </div>
          </div>

          {/* Before lg: price card in the hero. From lg: it straddles the seam in the bridge next to the premium row. */}
          <aside className="mx-auto mt-10 w-full max-w-md self-center px-0 lg:hidden" aria-label="Course pricing">
            <CatalogPriceCard course={course} />
          </aside>
        </div>
      </section>

      {/* Premium + price seam. Below lg: flows normally under the mobile price card.
          From lg: straddles the hero/content seam via absolute positioning. */}
      <div className="relative z-20 w-full lg:h-0">
        <div className="static mt-6 lg:absolute lg:inset-x-0 lg:top-0 lg:mt-0 lg:-translate-y-1/2">
          <div className="container relative mx-auto px-0">
            <div className="mx-auto flex max-w-6xl flex-col gap-5 pl-6 pr-6 sm:gap-6 md:pl-10 md:pr-2 lg:flex-row lg:items-center lg:justify-between lg:gap-5 lg:pl-10 lg:pr-0 xl:gap-6 xl:pl-12">
              <div className="relative z-40 min-w-0 w-full max-w-2xl">
                <div className="w-full overflow-hidden rounded-xl border border-slate-200/70 bg-white text-slate-900 shadow-lg ring-1 ring-slate-900/5">
                  <div className="flex flex-col md:flex-row md:items-stretch">
                    <div className="flex w-full items-center justify-center gap-3 bg-violet-600 px-3 py-4 text-white md:w-[120px] md:flex-col md:px-2 md:py-5">
                      <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 ring-1 ring-white/20">
                        <BadgeCheck className="h-5 w-5" />
                      </div>
                      <div className="text-sm font-black tracking-tight">Premium</div>
                    </div>

                    <div className="flex-1 border-t border-slate-200/70 p-3 md:min-w-0 md:border-t-0 md:border-l md:px-4 md:py-4">
                      <p className="text-sm font-semibold text-slate-900 leading-relaxed">{highlightLine}</p>
                      <p className="mt-1 text-xs text-slate-500">Highlights for this program</p>
                    </div>

                    <div className="hidden md:block w-px bg-slate-200/70" aria-hidden />

                    <div className="grid grid-cols-2 border-t border-slate-200/70 md:flex md:items-stretch md:border-t-0">
                      <div className="flex flex-col items-center justify-center gap-1.5 px-3 py-4 md:w-[150px] md:shrink-0 md:py-4">
                        <div className="text-2xl font-black tabular-nums text-slate-900">
                          {course.mockAverageRating.toFixed(1)}
                        </div>
                        <MockRatingStars rating={course.mockAverageRating} />
                        <div className="text-xs text-slate-500">
                          {course.mockRatingCount.toLocaleString("en-US")} rating{course.mockRatingCount === 1 ? "" : "s"}
                        </div>
                      </div>

                      <div className="hidden md:block w-px bg-slate-200/70" aria-hidden />

                      <div className="flex flex-col items-center justify-center gap-1.5 border-l border-slate-200/70 px-3 py-4 text-center md:w-[150px] md:shrink-0 md:border-l-0 md:py-4">
                        <Users className="h-4 w-4 text-slate-500" />
                        <div className="text-2xl font-black tabular-nums text-slate-900">
                          {formatCompactCount(course.mockLearnerCount)}
                        </div>
                        <div className="text-xs text-slate-500">learners</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <aside
                className="relative z-30 hidden w-full max-w-md shrink-0 self-center sm:max-w-md lg:ml-0 lg:block lg:w-[min(100%,360px)] lg:max-w-none lg:self-center lg:-mr-2 lg:translate-x-0.5 lg:translate-y-40 xl:-mr-4 xl:translate-y-44 2xl:-mr-5"
                aria-label="Course pricing"
              >
                <CatalogPriceCard course={course} />
              </aside>
            </div>
          </div>
        </div>
      </div>

      {/* Below-the-fold sections */}
      <section className="relative z-10 bg-white">
        <div className="container mx-auto px-0 py-10 md:py-14 lg:pt-36">
          <div className="mx-auto max-w-6xl pl-6 pr-0 md:pl-10 md:pr-2 lg:pl-12 lg:pr-0">
            <div className="min-w-0 lg:pr-[25rem]">
              <Link
                // A plain sign-in lands in the user portal (/courses), like signing in from any
                // other page; QA found returning here inconsistent. The buy button on this page
                // (CatalogPriceCard) still returns to checkout.
                href="/sign-in"
                className="group flex w-full items-center gap-4 border-b border-slate-200 bg-white py-4 text-left transition-colors hover:border-slate-300 hover:bg-slate-50/60 md:gap-6 md:py-5"
              >
                <div className="shrink-0">
                  <Image
                    src="/images/single-1.png"
                    alt="InaraX"
                    width={48}
                    height={48}
                    className="h-10 w-10 object-contain sm:h-12 sm:w-12"
                  />
                </div>
                <div className="min-w-0 flex-1 pr-1">
                  <p className="text-base font-bold text-slate-900">Earn a certificate from {course.providerName}</p>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
                    After completion of all courses in the{" "}
                    <span className="font-semibold text-violet-700 group-hover:underline">{certificatePathLabel}</span>{" "}
                    path, earn a professional certificate that you can share on social media, LinkedIn, resume, or CV.
                  </p>
                </div>
                <ArrowRight
                  className="h-5 w-5 shrink-0 text-slate-900 transition-transform group-hover:translate-x-0.5"
                  aria-hidden
                />
              </Link>

              <div className="mt-8 rounded-xl border border-slate-200 bg-white p-6 md:p-7 shadow-sm">
                <h2 className="text-lg md:text-xl font-black text-slate-900">What you&apos;ll learn</h2>
                <div className="mt-5 grid gap-6 md:grid-cols-2">
                  <ul className="space-y-3 text-sm text-slate-700">
                    {learnLeft.map((item) => (
                      <li key={item} className="flex gap-3">
                        <span className="mt-1.5 h-4 w-4 rounded-full border border-slate-300 flex items-center justify-center text-[10px] text-slate-600">
                          ✓
                        </span>
                        <span className="leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                  <ul className="space-y-3 text-sm text-slate-700">
                    {learnRight.map((item) => (
                      <li key={item} className="flex gap-3">
                        <span className="mt-1.5 h-4 w-4 rounded-full border border-slate-300 flex items-center justify-center text-[10px] text-slate-600">
                          ✓
                        </span>
                        <span className="leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <CourseContentSection levelModules={course.levelModules} />

              <CourseRequirementsDescription
                requirements={course.requirements}
                description={course.longDescription ?? course.description ?? undefined}
                outcomes={course.outcomes}
              />

              <CertificatePathCoursesSection
                providerName={course.providerName}
                currentCourseSlug={course.catalogSlug}
                courses={course.moreCourses}
              />
            </div>
          </div>
        </div>
      </section>

    </LandingPageShell>
  );
}

